import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import type { LeafRanges, SampleType, SoilSample } from '../../types/soil';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import {
  ANALYTE_BY_KEY, dayToIso, fmtNum, trendAnalytes, trendPoints, type TrendPoint,
} from '../../utils/soilAnalytes';

Chart.register(...registerables);

const LINE = '#2563eb';           // primary-600, validated against the card surface
const BAND = 'rgba(22, 163, 74, 0.10)';
const BAND_EDGE = 'rgba(22, 163, 74, 0.45)';
const INK_MUTED = '#6b7280';
const GRID = '#f1f5f9';

type Range = { low?: number | null; high?: number | null } | null | undefined;

/**
 * One nutrient over time at one place: a line through each reading, spaced
 * by real time, with the guide (soil) or the crop's standard (leaf) shaded
 * behind it. A figure below the lab's detection limit is a hollow point. The
 * readings are also offered as a table.
 *
 * The caller passes samples already narrowed to one place, one type and (for
 * soil) one depth (trendSamples), so the line never joins readings that are
 * not comparable.
 */
export default function NutrientTrendChart({
  samples, type, initial, standards, caption,
}: {
  samples: SoilSample[];
  type: SampleType;
  initial?: string;
  standards?: LeafRanges | null;
  /** What the samples are: "Gala on M9, 0–10 cm". */
  caption?: string;
}) {
  const timezone = useOrgTimezone();
  const options = useMemo(() => trendAnalytes(samples, type), [samples, type]);
  const [analyte, setAnalyte] = useState<string>(() =>
    (initial && options.some((a) => a.key === initial) ? initial : options[0]?.key) ?? '');
  useEffect(() => {
    if (!options.some((a) => a.key === analyte) && options[0]) setAnalyte(options[0].key);
  }, [options, analyte]);

  const meta = ANALYTE_BY_KEY.get(analyte);
  const points: TrendPoint[] = useMemo(() => trendPoints(samples, analyte), [samples, analyte]);
  const range: Range = type === 'leaf' ? standards?.[analyte] : meta?.guide;
  const rangeLabel = type === 'leaf' ? 'standard' : 'guide';

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    chartRef.current?.destroy();
    chartRef.current = null;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || points.length < 2) return;

    const first = points[0].day;
    const last = points[points.length - 1].day;
    const pad = Math.max(15, Math.round((last - first) * 0.04));
    const xMin = first - pad;
    const xMax = last + pad;
    const values = points.map((p) => p.value);
    const lo = range?.low ?? null;
    const hi = range?.high ?? null;
    const yVals = [...values, ...(lo != null ? [lo] : []), ...(hi != null ? [hi] : [])];
    const span = Math.max(...yVals) - Math.min(...yVals) || Math.abs(values[0]) * 0.2 || 1;
    const yMin = Math.max(0, Math.min(...yVals) - span * 0.15);
    const yMax = Math.max(...yVals) + span * 0.15;

    const bandLow = lo ?? yMin;
    const bandHigh = hi ?? yMax;
    const showBand = lo != null || hi != null;

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [
          ...(showBand ? [
            { label: 'band-low', data: [{ x: xMin, y: bandLow }, { x: xMax, y: bandLow }], borderColor: lo != null ? BAND_EDGE : 'transparent', borderWidth: 1, borderDash: [4, 4], pointRadius: 0, pointHitRadius: 0, fill: false },
            { label: 'band-high', data: [{ x: xMin, y: bandHigh }, { x: xMax, y: bandHigh }], borderColor: hi != null ? BAND_EDGE : 'transparent', borderWidth: 1, borderDash: [4, 4], pointRadius: 0, pointHitRadius: 0, fill: '-1', backgroundColor: BAND },
          ] : []),
          {
            label: meta?.label ?? analyte,
            data: points.map((p) => ({ x: p.day, y: p.value })),
            borderColor: LINE,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHitRadius: 14,
            pointBackgroundColor: points.map((p) => (p.qualifier ? '#ffffff' : LINE)),
            pointBorderColor: LINE,
            pointBorderWidth: 2,
            tension: 0,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        interaction: { mode: 'nearest', intersect: false, axis: 'x' },
        plugins: {
          legend: { display: false },
          tooltip: {
            filter: (item) => item.datasetIndex === (showBand ? 2 : 0),
            displayColors: false,
            callbacks: {
              title: (items) => formatLocalDateInTz(dayToIso(items[0].parsed.x ?? 0), timezone),
              label: (item) => {
                const p = points[item.dataIndex];
                const unit = meta?.unit ? ` ${meta.unit}` : '';
                return `${meta?.label ?? analyte}: ${p.qualifier ?? ''}${fmtNum(p.value)}${unit}${p.qualifier === '<' ? ' (below detection)' : ''}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: 'linear',
            min: xMin,
            max: xMax,
            grid: { display: false },
            border: { color: GRID },
            ticks: {
              color: INK_MUTED,
              font: { size: 10 },
              maxTicksLimit: 5,
              maxRotation: 0,
              // "Sep 2025" over years, "12 Sep" within one.
              callback: (v) => {
                const text = formatLocalDateInTz(dayToIso(Number(v)), timezone);
                return last - first > 400 ? text.replace(/^\d+\s/, '') : text.replace(/\s\d{4}$/, '');
              },
            },
          },
          y: {
            min: yMin,
            max: yMax,
            grid: { color: GRID },
            border: { display: false },
            ticks: { color: INK_MUTED, font: { size: 10 }, maxTicksLimit: 5, callback: (v) => fmtNum(Number(v)) },
          },
        },
      },
    });
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [points, range, meta, analyte, timezone]);

  if (!options.length) return null;

  const rangeText = range && (range.low != null || range.high != null)
    ? `Shaded: the ${rangeLabel}, ${range.low != null && range.high != null ? `${fmtNum(range.low)}–${fmtNum(range.high)}` : range.low != null ? `${fmtNum(range.low)} and above` : `up to ${fmtNum(range.high)}`}${meta?.unit ? ` ${meta.unit}` : ''}.`
    : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <label className="block text-sm min-w-0">
          <span className="text-neutral-700">Over time</span>
          <select
            className="block w-full max-w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
            value={analyte}
            onChange={(e) => setAnalyte(e.target.value)}
          >
            {options.map((a) => <option key={a.key} value={a.key}>{a.label}{a.unit ? ` (${a.unit})` : ''}</option>)}
          </select>
        </label>
        {caption && <p className="text-xs text-neutral-500 break-words">{caption}</p>}
      </div>
      <div className="relative h-48 w-full" role="img" aria-label={`${meta?.label ?? analyte} over time, ${points.length} readings`}>
        <canvas ref={canvasRef} />
      </div>
      <p className="text-xs text-neutral-500">
        {points.length} readings{points.some((p) => p.qualifier) ? '; a hollow point was below the lab\'s detection limit' : ''}.
        {rangeText && ` ${rangeText}`}
      </p>
      <details className="text-sm">
        <summary className="cursor-pointer text-xs text-primary-600">Show as a table</summary>
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500">
              <th className="font-medium py-1">Sampled</th>
              <th className="font-medium py-1 text-right">{meta?.label ?? analyte}{meta?.unit ? ` (${meta.unit})` : ''}</th>
            </tr>
          </thead>
          <tbody>
            {[...points].reverse().map((p) => (
              <tr key={p.sampleId} className="border-t border-neutral-100">
                <td className="py-1">{formatLocalDateInTz(p.date, timezone)}</td>
                <td className="py-1 text-right tabular-nums">{p.qualifier ?? ''}{fmtNum(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
